'use client'

import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react'
import { avatarFor } from '@/lib/utils'

type LazyAvatarProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'loading' | 'decoding'> & {
  src?: string | null
  seed: string
  rootMargin?: string
}

export function LazyAvatar({ src, seed, rootMargin = '200px', alt = '', ...rest }: LazyAvatarProps) {
  const placeholder = avatarFor(seed)
  const [resolvedSrc, setResolvedSrc] = useState<string>(placeholder)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const safeSrc =
    typeof window !== 'undefined' && window.location.protocol === 'https:' && src?.startsWith('http://')
      ? `https://${src.slice('http://'.length)}`
      : src

  useEffect(() => {
    // Nothing to lazy-load if there's no remote URL — show placeholder.
    if (!safeSrc) {
      setResolvedSrc(placeholder)
      return
    }

    const node = imgRef.current
    if (!node) {
      setResolvedSrc(safeSrc)
      return
    }

    // Safety: IntersectionObserver isn't available in JSDOM or very old
    // browsers. Fall back to immediate load.
    if (typeof IntersectionObserver === 'undefined') {
      setResolvedSrc(safeSrc)
      return
    }

    // If already showing a real image (not placeholder), update directly
    // to avoid flash when contact data arrives.
    if (resolvedSrc !== placeholder) {
      setResolvedSrc(safeSrc)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setResolvedSrc(safeSrc)
            observer.disconnect()
            break
          }
        }
      },
      { rootMargin },
    )
    observer.observe(node)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeSrc, seed, placeholder, rootMargin])

  return (
    <img
      ref={imgRef}
      src={resolvedSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        // Fall back to the local SVG placeholder if the remote
        // image fails. Avoids the browser's default broken-image
        // icon and keeps the grid looking clean.
        if (resolvedSrc !== placeholder) setResolvedSrc(placeholder)
      }}
      {...rest}
    />
  )
}
